import React, { useMemo, useRef } from "react";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Legend,
    Tooltip,
    Title,
} from "chart.js";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Legend,
    Tooltip,
    Title
);

export default function RatioTrends({ data }) {
    const chartRef = useRef();

    const {
        labels,
        dsCCC,
        dsDSO,
        dsDPO,
        dsDIO,
        dsWCDays,
        dsTradeWCDays,
        useSecondaryAxis,
    } = useMemo(() => {
        // ...existing code...
        // (same as your current useMemo block)
        // ...existing code...
        const seriesKeys = [
            "ccc",
            "dso",
            "dpo",
            "dio",
            "workingCapitalDays",
            "tradeWorkingCapitalDays",
        ];

        const allYearsSet = new Set();
        seriesKeys.forEach((k) =>
            (data?.[k] || []).forEach((d) => allYearsSet.add(String(d.year)))
        );
        const years = Array.from(allYearsSet).sort((a, b) => Number(a) - Number(b));

        const pick = (arr) =>
            years.map((y) => {
                const found = (arr || []).find((v) => String(v.year) === String(y));
                return found ? Number(found.value || 0) : 0;
            });

        const CCC = pick(data?.ccc);
        const DSO = pick(data?.dso);
        const DPO = pick(data?.dpo);
        const DIO = pick(data?.dio);
        const WCDays = pick(data?.workingCapitalDays);
        const TradeWCDays = pick(data?.tradeWorkingCapitalDays);

        const maxLeft = Math.max(
            1,
            ...CCC,
            ...DSO,
            ...DPO,
            ...WCDays,
            ...TradeWCDays
        );
        const maxDIO = Math.max(1, ...DIO);
        const useSecondary = maxDIO * 5 < maxLeft;

        // Debug logging
        console.log("Trends data:", { CCC, DSO, DPO, DIO, years });

        return {
            labels: years,
            dsCCC: {
                label: "CCC (Cash Conversion Cycle)",
                data: CCC,
                borderColor: "#f59e0b",
                backgroundColor: "#f59e0b",
                tension: 0.25,
                pointRadius: 4,
                borderWidth: 2,
                yAxisID: "y",
            },
            dsDSO: {
                label: "DSO (Days Sales Outstanding)",
                data: DSO,
                borderColor: "#dc2626",
                backgroundColor: "#dc2626",
                tension: 0.25,
                pointRadius: 4,
                borderWidth: 3,
                yAxisID: "y",
            },
            dsDPO: {
                label: "DPO (Days Payables Outstanding)",
                data: DPO,
                borderColor: "#2563eb",
                backgroundColor: "#2563eb",
                borderDash: [6, 4],
                tension: 0.25,
                pointRadius: 4,
                borderWidth: 2,
                yAxisID: "y",
            },
            dsDIO: {
                label: "DIO (Days Inventory Outstanding)",
                data: DIO,
                borderColor: "#9333ea",
                backgroundColor: "#9333ea",
                borderDash: [4, 4],
                tension: 0.25,
                pointRadius: 4,
                borderWidth: 2,
                yAxisID: useSecondary ? "y1" : "y",
            },
            dsWCDays: {
                label: "Working Capital Days (NWC/Revenue × 365)",
                data: WCDays,
                borderColor: "#10b981",
                backgroundColor: "#10b981",
                tension: 0.25,
                pointRadius: 3,
                yAxisID: "y",
            },
            dsTradeWCDays: {
                label: "Trade Working Capital Days ((AR+Inv−AP)/Sales × 365)",
                data: TradeWCDays,
                borderColor: "#f43f5e",
                backgroundColor: "#f43f5e",
                borderDash: [2, 6],
                tension: 0.25,
                pointRadius: 3,
                yAxisID: "y",
            },
            useSecondaryAxis: useSecondary,
        };
    }, [data]);

    const datasets = [dsCCC, dsDSO, dsDPO, dsDIO, dsWCDays, dsTradeWCDays].filter(
        (ds) => {
            const hasData = ds && Array.isArray(ds.data) && ds.data.some((v) => Number(v) !== 0);
            if (!hasData) {
                console.log("Filtered out dataset:", ds?.label, "data:", ds?.data);
            }
            return hasData;
        }
    );

    console.log("Final datasets count:", datasets.length, "datasets:", datasets.map(d => d.label));

    const chartData = {
        labels,
        datasets,
    };

    // Custom plugin to draw value labels above points when only one dataset is visible
    const valueLabelPlugin = {
        id: "valueLabelPlugin",
        afterDatasetsDraw: (chart) => {
            const visibleDatasets = chart.getSortedVisibleDatasetMetas();
            if (visibleDatasets.length !== 1) return;
            const ctx = chart.ctx;
            const meta = visibleDatasets[0];
            meta.data.forEach((point, i) => {
                const value = meta._dataset.data[i];
                if (value === 0) return;
                ctx.save();
                ctx.font = "bold 12px sans-serif";
                ctx.fillStyle = meta._dataset.borderColor || "#222";
                ctx.textAlign = "center";
                ctx.textBaseline = "bottom";
                ctx.fillText(
                    value.toFixed(2),
                    point.x,
                    point.y - 8
                );
                ctx.restore();
            });
        },
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
            legend: { position: "bottom" },
            valueLabels: false,
            title: {
                display: true,
                text:
                    "Working Capital Metrics — CCC, DSO, DPO, DIO, WC Days (NWC/Sales), Trade WC Days",
            },
            tooltip: {
                callbacks: {
                    label: (ctx) =>
                        `${ctx.dataset.label}: ${Number(ctx.parsed.y).toFixed(2)} days`,
                },
            },
            valueLabelPlugin: {}, // activate plugin
        },
        scales: {
            x: {
                title: { display: true, text: "Year" },
                grid: { display: false },
            },
            y: {
                title: { display: true, text: "Days" },
                ticks: { precision: 0 },
            },
            y1: useSecondaryAxis
                ? {
                    position: "right",
                    title: { display: true, text: "DIO (Days)" },
                    grid: { drawOnChartArea: false },
                    suggestedMin: 0,
                }
                : undefined,
        },
        elements: {
            point: { radius: 2, hoverRadius: 4 },
        },
    };

    return (
        <div style={{ height: 360, width: "100%" }}>
            <Line ref={chartRef} data={chartData} options={options} plugins={[valueLabelPlugin]} />
        </div>
    );
}